import {Action, getModule, Module, Mutation, VuexModule} from "vuex-module-decorators";
import store from "@/store";
import {PriceListState} from "@/interfaces/state";
import {PriceList} from "@/interfaces/entity";
import {IdMapper} from "@/interfaces/state/base";
import {PaginationMeta} from "@/interfaces/common";
import {PriceListParams} from "@/modules/pricelists/params";
import { union } from "lodash";
import {
    createPriceList,
    deletePriceList,
    listPriceList,
    singlePriceList,
    updatePriceList
} from "@/modules/pricelists/services";

@Module({ dynamic: true, name: "priceList", store })
class PriceListModule extends VuexModule implements PriceListState {
    data: IdMapper<PriceList> = {};
    ids: number[] = [];
    meta: PaginationMeta = new PaginationMeta();
    params: PriceListParams = new PriceListParams();
    idByPostOffice = {} as any;
    loadedIds: number[] = [];

    @Mutation
    private UPDATE_PRICE_LIST_PARAMS(payload: any) {
        this.params = payload;
    }

    @Mutation
    private MAP_PRICE_LIST({payload, refresh = true}) {
        const idMapper: IdMapper<PriceList> = {};
        this.meta = payload.meta;
        this.loadedIds = payload.datas?.map((priceList: PriceList, index: number) => {
            idMapper[priceList.id!] = {...priceList, stt: index + 1, isDeleting: false} as any;
            this.idByPostOffice = {
                ...this.idByPostOffice,
                [priceList.postOfficeRowGuid]: union(
                    [priceList.id],
                    this.idByPostOffice[priceList.postOfficeRowGuid] || []
                )
            }
            return priceList.id;
        });
        this.ids = union(this.loadedIds, this.ids);
        this.data = {...this.data, ...idMapper};
    }

    @Mutation
    private SET_PRICE_LIST(payload: PriceList) {
        this.ids = union(this.ids, [ payload.id ]);
        this.data = { ...this.data, [payload.id]: {...payload, isDeleting: false} }
        this.idByPostOffice = {
            ...this.idByPostOffice,
            [payload.postOfficeRowGuid]: union(
                [payload.id],
                this.idByPostOffice[payload.postOfficeRowGuid] || [],
            )
        }
    }

    @Mutation
    private DELETE_PRICE_LIST(payload: PriceList) {
        if (!this.data[payload.id] || !this.ids.includes(payload.id)) return;
        this.ids = this.ids.filter((id: number) => id !== payload.id);
        delete this.data[payload.id];
    }

    @Mutation
    private START_DELETE_PRICE_LIST(id: number) {
        this.data[id].isDeleting = true;
    }

    @Mutation
    private END_DELETE_PRICE_LIST(id: number) {
        this.data[id].isDeleting = false;
    }

    @Action({ rawError: true })
    public updatePriceListParams(payload: any) {
        this.UPDATE_PRICE_LIST_PARAMS(payload);
    }

    @Action({ rawError: true })
    public addPriceList(payload: PriceList) {
        this.SET_PRICE_LIST(payload);
    }

    @Action({ rawError: true })
    public async createPriceList(form: any) {
        const { data } = await createPriceList(form);
        this.SET_PRICE_LIST(data.data);
    }

    @Action({ rawError: true })
    public async listPriceList({params = new PriceListParams(), refresh = true}) {
        const { data } = await listPriceList(params);
        this.MAP_PRICE_LIST({payload: data, refresh} );
    }

    @Action({ rawError: true })
    public async singlePriceList(id: number) {
        const { data } = await singlePriceList(id);
        this.SET_PRICE_LIST(data.data);
        return data.data;
    }

    @Action({ rawError: true })
    public async updatePriceList(dataReq: any) {
        const { data } = await updatePriceList(dataReq.id, dataReq);
        this.SET_PRICE_LIST(data.data);
    }

    @Action({ rawError: true })
    public async deletePriceList(id: number) {
        this.START_DELETE_PRICE_LIST(id);
        const { data } = await deletePriceList(id);
        this.END_DELETE_PRICE_LIST(id);
        this.DELETE_PRICE_LIST(data.data);
    }
}

export const PriceListStore = getModule(PriceListModule);
